// The "raw view" a connector can ask for with a `gundi:raw_view` annotation on an
// action's ui_schema: { title, map: { <legacy key>: <field path> }, drop?, mask?,
// unwrap? }.
// The user pastes a legacy config as JSON and the form fills from it; the form's
// saved values show back in the legacy key names. Keys the map doesn't name are
// kept as they are, at the top level of the config, unless the connector lists
// them in `drop`; keys in `mask` are kept with their value hidden. `unwrap` maps
// a legacy key to the object field that holds its value, for lists the legacy
// config stored as objects ([{"id": "a"}] becomes ["a"]). Pure functions only —
// the view itself lives in ActionSectionForm.

export const RAW_VIEW_KEY = 'gundi:raw_view';

export type RawView = {
  title: string;
  map: Record<string, string>;
  drop: string[];
  mask: string[];
  unwrap: Record<string, string>;
};

// What a masked key is saved as, so the secret never reaches the config.
export const MASKED_VALUE = '*******';

type Translate = (key: string, options?: any) => string;

export const rawViewOf = (action: any): RawView | undefined => {
  const raw = action?.ui_schema?.[RAW_VIEW_KEY];
  if (!raw || typeof raw !== 'object') return undefined;
  if (!raw.map || typeof raw.map !== 'object' || Array.isArray(raw.map))
    return undefined;
  const map = Object.fromEntries(
    Object.entries(raw.map).filter(
      ([key, path]) => key && typeof path === 'string' && path,
    ),
  ) as Record<string, string>;
  if (Object.keys(map).length === 0) return undefined;
  const keys = (list: any): string[] =>
    Array.isArray(list)
      ? list.filter((k: any) => typeof k === 'string' && k)
      : [];
  return {
    title: typeof raw.title === 'string' && raw.title ? raw.title : 'Raw',
    map,
    drop: keys(raw.drop),
    mask: keys(raw.mask),
    unwrap:
      raw.unwrap && typeof raw.unwrap === 'object' && !Array.isArray(raw.unwrap)
        ? (Object.fromEntries(
            Object.entries(raw.unwrap).filter(
              ([key, field]) => key && typeof field === 'string' && field,
            ),
          ) as Record<string, string>)
        : {},
  };
};

// rjsf has no use for the annotation; it is ours alone.
export const withoutRawView = (uiSchema: any) => {
  if (!uiSchema || !(RAW_VIEW_KEY in uiSchema)) return uiSchema;
  const { [RAW_VIEW_KEY]: _omitted, ...rest } = uiSchema;
  return rest;
};

const refName = (node: any): string | undefined => {
  const ref = node?.$ref ?? node?.allOf?.find((s: any) => s?.$ref)?.$ref;
  return typeof ref === 'string' ? ref.split('/').pop() : undefined;
};

// The schema of the field at a dotted path. pydantic wraps nested models as
// { allOf: [{ $ref }] }, so each step follows that into `definitions`.
export const fieldSchemaAt = (schema: any, path: string): any => {
  let node = schema;
  for (const part of path.split('.')) {
    const name = refName(node);
    if (name) node = schema?.definitions?.[name];
    node = node?.properties?.[part];
    if (!node) return undefined;
  }
  const name = refName(node);
  return name ? { ...schema?.definitions?.[name], ...node } : node;
};

const getAt = (data: any, path: string) =>
  path
    .split('.')
    .reduce((node, key) => (node == null ? undefined : node[key]), data);

// Immutable set; `undefined` removes the key so the field falls back to its default.
const setAt = (data: any, path: string, value: any): any => {
  const [head, ...rest] = path.split('.');
  const base = data && typeof data === 'object' ? data : {};
  if (rest.length === 0) {
    const { [head]: _old, ...others } = base;
    return value === undefined ? others : { ...others, [head]: value };
  }
  return { ...base, [head]: setAt(base[head], rest.join('.'), value) };
};

const typeError = (
  key: string,
  field: any,
  value: any,
  t: Translate,
): string | undefined => {
  switch (field?.type) {
    case 'boolean':
      return typeof value === 'boolean'
        ? undefined
        : t('{{key}} should be true or false.', { key });
    case 'string':
      return typeof value === 'string'
        ? undefined
        : t('{{key}} should be text.', { key });
    case 'integer':
      return Number.isInteger(value)
        ? undefined
        : t('{{key}} should be a whole number.', { key });
    case 'number':
      return typeof value === 'number'
        ? undefined
        : t('{{key}} should be a number.', { key });
    case 'array':
      return Array.isArray(value) &&
        (field.items?.type !== 'string' ||
          value.every((v: any) => typeof v === 'string'))
        ? undefined
        : t('{{key}} should be a list of text values.', { key });
    default:
      return undefined;
  }
};

// Own keys only: a legacy key named like an inherited property (`constructor`,
// `toString`) is still an ordinary key.
const has = (obj: any, key: string) =>
  !!obj && Object.prototype.hasOwnProperty.call(obj, key);

// Top-level config keys that are not form fields: the legacy keys the raw view
// keeps. Form field names stay out, so a kept key can never overwrite a field.
const isKeptKey = (key: string, schema: any, raw: RawView) =>
  !has(schema?.properties, key) &&
  !has(raw.map, key) &&
  !raw.drop.includes(key);

// A legacy list stored as objects, read as the values in `field`: each object
// gives its `field`, plain strings stay as they are, so mixed lists work. Null
// when an item is neither.
const unwrapList = (value: any, field: string): string[] | null => {
  if (!Array.isArray(value)) return value;
  const out: string[] = [];
  for (const item of value) {
    const inner =
      item && typeof item === 'object' && !Array.isArray(item)
        ? item[field]
        : item;
    if (typeof inner !== 'string') return null;
    out.push(inner);
  }
  return out;
};

// Legacy JSON -> form data. Mapped keys that are missing or null clear their
// field, so deleting a line in the raw view resets that setting; fields the map
// doesn't name keep their value. Every other key is kept as it is (masked keys
// with their value hidden) except those in `drop`, and a kept key whose line is
// deleted is removed, so the config holds what the raw view shows.
export const v1ToForm = (
  text: string,
  raw: RawView,
  schema: any,
  formData: any,
  t: Translate,
): { data: any } | { error: string } => {
  let parsed: any;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { error: t("This isn't valid JSON.") };
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
    return {
      error: t(
        'This should be a JSON object, for example {"update_schema": true}.',
      ),
    };
  let data = formData ?? {};
  for (const [legacyKey, path] of Object.entries(raw.map)) {
    let value = parsed[legacyKey];
    if (value === undefined || value === null) {
      data = setAt(data, path, undefined);
      continue;
    }
    // Mapped keys only: a kept key has no field to check the converted list
    // against, so it is stored as pasted.
    const unwrapField = raw.unwrap[legacyKey];
    if (unwrapField) {
      const unwrapped = unwrapList(value, unwrapField);
      if (unwrapped === null)
        return {
          error: t('{{key}} should be a list of ids.', { key: legacyKey }),
        };
      value = unwrapped;
    }
    const error = typeError(legacyKey, fieldSchemaAt(schema, path), value, t);
    if (error) return { error };
    data = setAt(data, path, value);
  }
  // Kept keys are set by name, not as paths: a legacy key with a dot in it is
  // still one key.
  // Dropped keys go from the stored config too: the raw view never shows them,
  // so nobody could remove them otherwise.
  const isDropped = (key: string) =>
    raw.drop.includes(key) && !has(schema?.properties, key);
  const kept = Object.fromEntries(
    Object.entries(data).filter(
      ([key]) =>
        !isDropped(key) && (!isKeptKey(key, schema, raw) || has(parsed, key)),
    ),
  );
  for (const [key, value] of Object.entries(parsed))
    if (isKeptKey(key, schema, raw))
      kept[key] =
        raw.mask.includes(key) && value != null ? MASKED_VALUE : value;
  return { data: kept };
};

// Form data -> legacy JSON: the mapped keys in map order, then the kept keys.
// Unset mapped values and empty lists are left out; "" and false are real
// choices and stay.
export const formToV1 = (
  formData: any,
  raw: RawView,
  schema: any,
): Record<string, any> => {
  const out: Record<string, any> = {};
  for (const [legacyKey, path] of Object.entries(raw.map)) {
    const value = getAt(formData, path);
    if (value === undefined || value === null) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    out[legacyKey] = value;
  }
  for (const [key, value] of Object.entries(formData ?? {}))
    if (isKeptKey(key, schema, raw))
      out[key] = raw.mask.includes(key) && value != null ? MASKED_VALUE : value;
  return out;
};

export const formatV1 = (formData: any, raw: RawView, schema: any): string =>
  JSON.stringify(formToV1(formData, raw, schema), null, 2);
